import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-location';
import { isFunction } from 'lodash-es';
import { getLocationPortalDetails } from '@frontend/api-weave-pay';
import { HttpError } from '@frontend/fetch';
import { useTranslation } from '@frontend/i18n';
import { FailureHandler } from '../components';
import { WeavePayLocationInfo } from '../types/payment-request';

interface UseLocationPortalParams {
  locationSlug: string;
  onFailure?: FailureHandler;
}

export const useLocationPortal = ({ locationSlug, onFailure }: UseLocationPortalParams) => {
  const navigate = useNavigate();
  const { t } = useTranslation('weave-pay');

  const [portalData, setPortalData] = useState<WeavePayLocationInfo>({
    name: '',
    id: '',
    email: '',
    statementDescriptor: '',
    locationLogo: '',
  });

  useEffect(() => {
    if (portalData.id && !portalData.onBehalfOf) {
      onFailure?.(new Error(t('empty onBehalfOf')));
    }
  }, [portalData]);

  useEffect(() => {
    const getMerchantInfo = async () => {
      try {
        const data = await getLocationPortalDetails(locationSlug);
        const locationData: WeavePayLocationInfo = {
          name: data.location?.name,
          id: data.location?.id,
          email: data.location?.email,
          statementDescriptor: data.location?.statementDescriptor,
          locationLogo: data.links?.locationLogo,
          phoneNumber: data.location?.phoneNumber,
          onBehalfOf: data.onBehalfOf,
          onlineBillPayEnabled: data.onlineBillPayEnabled,
        };
        setPortalData(locationData);
      } catch (err) {
        if (!isFunction(onFailure)) navigate({ to: '/error' });
        else if (err instanceof HttpError) onFailure(err);
      }
    };
    getMerchantInfo();
  }, [locationSlug]);

  return { portalData };
};
